import { isPortfolioType } from './../../../../shared/account-types.js';
import {
	belowZeroPath,
	buildBand,
	crossingBounds,
	interpAt,
	normalize,
	pt,
	tOf,
	toPath,
	VB_H,
	VB_W,
	yPos,
} from './sparkline-geometry.js';

const SAMPLE_N = 800;
const MIN_POINTS = 1;

/**
 * Convert sorted { date, value } points to parallel arrays.
 * @param {Array<{ date: string, value: number }>} rows
 * @returns {{ dates: string[], values: number[] }}
 */
export function snapshotsToSeries(rows) {
	const sorted = [...rows].sort((a, b) => a.date.localeCompare(b.date));
	return { dates: sorted.map((r) => r.date), values: sorted.map((r) => r.value) };
}

/**
 * Sample a series at `opts.n` uniformly spaced times across [opts.t0, opts.t1]
 * (defaults to the series' own range). mode 'linear' interpolates between
 * snapshots (valuations); mode 'step' holds each value flat until the next
 * date then jumps (balances/principal). Values clamp flat outside the series'
 * own range.
 * @param {string[]} dates
 * @param {number[]} values
 * @param {'linear' | 'step'} mode
 * @param {{ n?: number, t0?: number, t1?: number }} [opts]
 * @returns {number[]}
 */
export function timeGridSample(dates, values, mode, opts = {}) {
	const n = opts.n ?? SAMPLE_N;
	const ts = dates.map(tOf);
	const t0 = opts.t0 ?? ts[0];
	const t1 = opts.t1 ?? ts[ts.length - 1];
	const span = Math.max(t1 - t0, 1);
	const out = new Array(n);
	for (let k = 0; k < n; k++) {
		const tk = t0 + (span * k) / (n - 1);
		out[k] = mode === 'step' ? stepAt(tk) : linearAt(tk);
	}
	function linearAt(tk) {
		if (tk <= ts[0]) { return values[0]; }
		if (tk >= ts[ts.length - 1]) { return values[values.length - 1]; }
		let lo = 0;
		let hi = ts.length - 1;
		while (lo < hi - 1) {
			const mid = (lo + hi) >> 1;
			if (ts[mid] <= tk) { lo = mid; } else { hi = mid; }
		}
		const f = (tk - ts[lo]) / (ts[hi] - ts[lo] || 1);
		return values[lo] + (values[hi] - values[lo]) * f;
	}
	function stepAt(tk) {
		let lo = 0;
		let hi = ts.length - 1;
		while (lo < hi) {
			const mid = (lo + hi + 1) >> 1;
			if (ts[mid] <= tk) { lo = mid; } else { hi = mid - 1; }
		}
		return ts[lo] <= tk ? values[lo] : values[0];
	}
	return out;
}

/**
 * Single series -> { line, area } with a zero baseline; the area floods to
 * the bottom edge. When values go below zero, returns zeroLine (horizontal
 * at y=0) and belowArea (red fill for the region between the curve and the
 * zero line where values are negative).
 * @param {number[]} values
 * @returns {{ line: string, area: string, zeroLine: string, belowArea: string }}
 */
export function singleSpark(values) {
	const { min, range } = normalize(values);
	const line = toPath(values, min, range);
	const area = `${line} L${pt(VB_W, VB_H)} L${pt(0, VB_H)} Z`;
	const zy = yPos(0, min, range);
	const zeroLine = min < 0
		? `M${pt(0, zy)} L${pt(VB_W, zy)}`
		: '';
	const belowArea = min < 0 ? belowZeroPath(values, min, range, zy) : '';
	return { line, area, zeroLine, belowArea };
}

/**
 * Two series (balance vs principal) -> band paths with a zero baseline.
 * The region between the curves splits into green (profit) and red (loss)
 * segments wherever they cross. Includes zeroLine when values go below zero.
 * @param {number[]} balance
 * @param {number[]} principal
 * @returns {{ balanceLine: string, principalLine: string, principalArea: string, profit: string[], loss: string[], zeroLine: string }}
 */
export function twoBandSpark(balance, principal) {
	const { min, range } = normalize(balance.concat(principal));
	const ctx = { balance, principal, min, range, n: balance.length };
	const balanceLine = toPath(balance, min, range);
	const principalLine = toPath(principal, min, range);
	const principalArea = `${principalLine} L${pt(VB_W, VB_H)} L${pt(0, VB_H)} Z`;
	const profit = [];
	const loss = [];
	const bounds = crossingBounds(balance, principal);
	for (let k = 0; k < bounds.length - 1; k++) {
		const b0 = bounds[k];
		const b1 = bounds[k + 1];
		if (b1 - b0 < 1e-6) { continue; }
		const mid = (b0 + b1) / 2;
		const isProfit = interpAt(balance, mid) > interpAt(principal, mid);
		const d = buildBand(b0, b1, isProfit, ctx);
		(isProfit ? profit : loss).push(d);
	}
	const zy = yPos(0, min, range);
	const zeroLine = min < 0
		? `M${pt(0, zy)} L${pt(VB_W, zy)}`
		: '';
	return { balanceLine, principalLine, principalArea, profit, loss, zeroLine };
}

/**
 * Align valuation (linear) and history (step) series onto a common time
 * grid across the union of their date ranges, sampled densely.
 * @param {Array<{ date: string, value: number }>} valuationRows
 * @param {Array<{ date: string, value: number }>} historyRows
 * @returns {{ balance: number[], principal: number[] }}
 */
function sampleTwoSeries(valuationRows, historyRows) {
	const bal = snapshotsToSeries(valuationRows);
	const prin = snapshotsToSeries(historyRows);
	const t0 = Math.min(tOf(bal.dates[0]), tOf(prin.dates[0]));
	const t1 = Math.max(tOf(bal.dates[bal.dates.length - 1]), tOf(prin.dates[prin.dates.length - 1]));
	return {
		balance: timeGridSample(bal.dates, bal.values, 'linear', { n: SAMPLE_N, t0, t1 }),
		principal: timeGridSample(prin.dates, prin.values, 'step', { n: SAMPLE_N, t0, t1 }),
	};
}

/**
 * @param {Array<{ date: string }>} rows
 * @returns {number} number of distinct dates
 */
function distinctDates(rows) {
	return new Set(rows.map((r) => r.date)).size;
}

/**
 * Decide what to render for an account and compute its chart paths.
 * Non-investment accounts use history (derived balance) with step mode.
 * Investment accounts use valuations (stored) with linear mode and
 * history (derived principal) with step mode.
 * @param {string} accountType
 * @param {Array<{ date: string, value: number }>} history - derived balance or principal
 * @param {Array<{ date: string, value: number }>} valuations - stored valuations
 * @returns {null | { kind: 'single', area: string, line: string, zeroLine: string, belowArea: string } | { kind: 'two', balanceLine: string, principalLine: string, principalArea: string, profit: string[], loss: string[], zeroLine: string }}
 */
export function buildSparklineData(accountType, history, valuations) {
	if (isPortfolioType(accountType)) {
		const valPoints = valuations || [];
		const hisPoints = history || [];
		if (distinctDates(valPoints) < MIN_POINTS || distinctDates(hisPoints) < MIN_POINTS) {
			return null;
		}
		const series = sampleTwoSeries(valPoints, hisPoints);
		return { kind: 'two', ...twoBandSpark(series.balance, series.principal) };
	}
	const hisPoints = history || [];
	if (distinctDates(hisPoints) < MIN_POINTS) { return null; }
	const series = snapshotsToSeries(hisPoints);
	const values = timeGridSample(series.dates, series.values, 'step');
	return { kind: 'single', ...singleSpark(values) };
}
